import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './CareerPathPanel.css';
import { CareerPathPanelProps, CareerPathProgress } from '../../types';
import { printCareerPathSheet, CareerPathPrintLevel } from './careerPathPrint';

const API_URL = (process.env.REACT_APP_API_URL as string) || 'http://localhost:3001/api';

const CAREER_LEVELS: CareerPathPrintLevel[] = [
  {
    level: 'L1',
    title: 'Junior Developer',
    description: 'Entry-level position requiring basic understanding of development tools, mobile app and web development concepts, and one programming language.',
    subLevels: [
      {
        code: 'L1-1',
        description: 'Basic Understanding of Development Tools and Web Fundamentals (SSH for remote server access, Git for version control and collaboration, HTML for structure, CSS for styling and layout)'
      },
      {
        code: 'L1-2',
        description: 'Understanding of basic Coding Concepts and Principles (DRY, KISS, Code Organization, Functions, Variables, Control Flow, Comments and Documentation)'
      },
      {
        code: 'L1-3',
        description: 'Understanding of basic Mobile App Development Coding Concepts (State Management, Async/Await, Mobile UI/UX Principles)'
      },
      {
        code: 'L1-4',
        description: 'Demonstrated ability to write functional code in ONE language (PHP/Laravel for server-side development or React/Vue/JS for client-side development, including understanding of syntax, data types, and functions)'
      },
      {
        code: 'L1-5',
        description: 'Understanding use of SiteAdmin CMS and admin panel frameworks (Bagisto for Laravel e-commerce, Filament for Laravel admin panels, or other modern CMS platforms)'
      },
      {
        code: 'L1-6',
        description: 'Understands foundational AI programming concepts, including prompts, tokens, model inputs and ' +
          'outputs, embeddings, classification, and why AI systems require validation beyond normal feature testing'
      },
      {
        code: 'L1-7',
        description: 'Demonstrates the ability to build a simple AI-powered feature using an existing model API, ' +
          'including sending structured input, parsing structured output, and handling basic failure cases'
      }
    ],
    goal: 'Demonstrates practical application by implementing JavaScript frameworks, converting designs into functional websites using CMS platforms, and writing clear code documentation.'
  },
  {
    level: 'L2',
    title: 'Developer',
    description: 'Growing software developer with foundational skills in JavaScript frameworks, CMS implementation, documentation, and can complete tasks with minor guidance.',
    subLevels: [
      {
        code: 'L2-1',
        description: 'Demonstrates the ability to implement and use JavaScript frameworks (React, React Native/Expo, or Vue) AND can complete tasks with minor guidance'
      },
      {
        code: 'L2-2',
        description: 'Demonstrates the ability to convert designs (wireframes, mockups, UI/UX specs) into functional web applications, mobile applications, or CMS-based sites, including component implementation, responsive layouts, and basic interactivity.'
      },
      {
        code: 'L2-3',
        description: 'Demonstrates the ability to document existing code (code comments, README files, function documentation) or write self-documenting code with clear naming conventions, following in-house documentation standards and best practices'
      },
      {
        code: 'L2-4',
        description: 'Demonstrates basic debugging and problem-solving skills, can identify and resolve common errors independently, and understands error handling and basic testing practices'
      },
      {
        code: 'L2-5',
        description: 'Demonstrates the ability to work with existing codebases, understand code structure and patterns, and integrate API calls and third-party libraries with minimal assistance'
      },
      {
        code: 'L2-6',
        description: 'Demonstrates the ability to integrate LLM or embedding APIs into application features, including ' +
          'prompt construction, schema validation, error handling, logging, and safe handling of user or company data'
      },
      {
        code: 'L2-7',
        description: 'Demonstrates the ability to build a basic retrieval or semantic search workflow using embeddings, ' +
          'chunking, similarity scoring, and clear tests that verify the AI feature returns useful results'
      }
    ],
    goal: 'Demonstrates ability to build complete production-ready features independently using React, React Native/Expo, or Vue, adapts to complex codebases and architectures, implements complex custom solutions, and effectively communicates with clients and stakeholders.'
  },
  {
    level: 'L3',
    title: 'Senior Developer',
    description: 'Experienced developer who works independently, builds complete production-ready applications, and communicates effectively with clients and stakeholders.',
    subLevels: [
      {
        code: 'L3-1',
        description: 'Demonstrates mastery of CSS and JavaScript, including versatility across CSS frameworks (Tailwind, Bootstrap, styled-components), cross-browser/device debugging, responsive design, and advanced JavaScript (ES6+, async/await, design patterns, performance optimization)'
      },
      {
        code: 'L3-2',
        description: 'Demonstrates the ability to build complete, production-ready applications independently using React, React Native/Expo, or Vue, including advanced component patterns, state management, and performance optimization'
      },
      {
        code: 'L3-3',
        description: 'Demonstrates the ability to adapt to and extend complex codebases, understand and work with complex code architectures, implement new features within established patterns, and provide ongoing support and maintenance'
      },
      {
        code: 'L3-4',
        description: 'Demonstrates the ability to implement and customize complex custom solutions, integrate APIs and third-party services, and solve technical challenges independently'
      },
      {
        code: 'L3-5',
        description: 'Demonstrates the ability to master client communications, translate technical concepts for non-technical stakeholders, gather requirements effectively, and provide clear project updates and timelines'
      },
      {
        code: 'L3-6',
        description: 'Demonstrates the ability to design production-ready AI workflows, including prompt templates, ' +
          'structured outputs, retrieval context, fallback behavior, model selection, and monitoring for bad responses'
      },
      {
        code: 'L3-7',
        description: 'Demonstrates the ability to evaluate AI feature quality with test datasets, regression checks, ' +
          'human review loops, and metrics for relevance, accuracy, latency, cost, and failure modes'
      }
    ],
    goal: 'Demonstrates mastery of SQL and database optimization, expert debugging skills across the stack, ability to optimize performance and launch production applications, build custom APIs, understand infrastructure fundamentals, develop deep expertise in a specialized area, and effectively manage client relationships and technical communications.'
  },
  {
    level: 'L4',
    title: 'Staff Engineer',
    description: 'Expert-level developer with advanced skills in SQL, debugging, performance optimization, API development, and infrastructure fundamentals. Works on complex technical challenges and mentors other developers.',
    subLevels: [
      {
        code: 'L4-1',
        description: 'Demonstrates mastery of Laravel Eloquent ORM and database optimization, including complex relationships, eager loading optimization, N+1 problem prevention, query scopes, accessors/mutators, strategic use of raw SQL when needed, database migrations and index optimization, schema design (normalized/denormalized), and efficient large dataset operations'
      },
      {
        code: 'L4-2',
        description: 'Demonstrates expert debugging skills, can diagnose complex distributed system issues across microservices, use advanced debugging tools (profiling, tracing, memory analysis), resolve critical production incidents, and optimize performance bottlenecks in high-traffic applications'
      },
      {
        code: 'L4-3',
        description: 'Demonstrates the ability to optimize application speed, performance, and scalability for high-traffic production environments, implement sophisticated caching strategies (Redis, Memcached, CDN), set up comprehensive performance monitoring and alerting, and ensure applications can handle significant load'
      },
      {
        code: 'L4-4',
        description: 'Demonstrates the ability to implement and integrate complex third-party APIs and services at enterprise scale, handle OAuth2/OIDC authentication flows, manage webhook integrations, handle rate limiting and retry strategies, and troubleshoot complex integration issues in production'
      },
      {
        code: 'L4-5',
        description: 'Demonstrates the ability to architect and build production-grade custom APIs from scratch, design scalable RESTful services, implement robust authentication/authorization (JWT, API keys, role-based access), build comprehensive API documentation, handle versioning, rate limiting, and create reusable enterprise-level utilities and libraries'
      },
      {
        code: 'L4-6',
        description: 'Demonstrates deep understanding of DNS, IP addressing, network fundamentals, server configuration, load balancing, reverse proxies, SSL/TLS certificates, containerization basics, and can manage basic infrastructure deployments'
      },
      {
        code: 'L4-7',
        description: 'Demonstrates deep expertise in a specific technical domain or technology stack, becomes the go-to resource for solving complex problems in that area, contributes to technical architecture decisions, and can build sophisticated solutions from the ground up in their domain'
      },
      {
        code: 'L4-8',
        description: 'Demonstrates the ability to architect advanced AI systems, including hybrid retrieval, reranking, ' +
          'multi-step reasoning workflows, agentic tool use, privacy controls, evaluation strategy, and cost management'
      },
      {
        code: 'L4-9',
        description: 'Demonstrates the ability to implement custom AI ranking or retrieval approaches, such as ' +
          'combining cosine search, BM25, rerankers, and evidence reconstruction patterns similar to CFS-R'
      }
    ],
    goal: 'Demonstrates ability to design and architect database systems, create scalable software architectures, manage deployment pipelines and CI/CD, administer infrastructure and servers, implement complex security solutions (SSO), work with low-level protocols and enterprise systems, and provide technical leadership through mentoring and code reviews.'
  },
  {
    level: 'L5',
    title: 'Lead Software Engineer',
    description: 'The highest technical tier for individual contributors, responsible for system architecture, complex technical problem-solving, infrastructure management, deployment pipelines, and mentoring other engineers.',
    subLevels: [
      {
        code: 'L5-1',
        description: 'Demonstrates the ability to design and architect enterprise-scale database systems, design complex multi-tenant data models, implement sharding and replication strategies, optimize for extreme scale and performance, and make critical decisions on database technology choices (SQL vs NoSQL, data warehouses, etc.)'
      },
      {
        code: 'L5-2',
        description: 'Demonstrates the ability to architect and design distributed, scalable software systems from the ground up, establish organization-wide coding standards and architectural patterns, design microservices architectures, create technical roadmaps, make strategic technology decisions, and design systems that can scale horizontally to handle a large set of users'
      },
      {
        code: 'L5-3',
        description: 'Demonstrates the ability to design and implement comprehensive deployment strategies across all platforms (iOS, Android, web), architect and build robust CI/CD pipelines for complex multi-service applications, establish QA processes and testing strategies, ensure zero-downtime deployments, and manage production incidents at scale'
      },
      {
        code: 'L5-4',
        description: 'Demonstrates the ability to architect and manage complex infrastructure (cloud or on-premise), design high-availability and disaster recovery solutions, implement infrastructure as code, configure and optimize container orchestration (Kubernetes, Docker Swarm), manage complex networking (VPCs, VPNs, firewalls), implement monitoring and observability at scale, and troubleshoot critical infrastructure failures'
      },
      {
        code: 'L5-5',
        description: 'Demonstrates the ability to architect and implement enterprise-grade SSO and identity management systems, design complex multi-protocol authentication systems (SAML, OAuth2, OIDC, LDAP), implement advanced security patterns (zero-trust, mTLS, certificate management), design and build custom authentication/authorization frameworks, and ensure compliance with security standards (SOC2, GDPR, HIPAA, PCI)'
      },
      {
        code: 'L5-6',
        description: 'Demonstrates the ability to architect and build enterprise-level API platforms, design complex system integrations, manipulate binary data formats and protocols, work with low-level network protocols, reverse-engineer undocumented APIs, build middleware and message brokers, design event-driven architectures, and integrate with legacy enterprise systems.'
      },
      {
        code: 'L5-7',
        description: 'Demonstrates the ability to lead and mentor teams of developers, establish engineering culture and best practices, conduct architectural reviews and technical design reviews, drive organization-wide technical initiatives, make critical technology stack decisions, resolve complex technical disputes, and provide strategic technical guidance that impacts product direction and business outcomes'
      },
      {
        code: 'L5-8',
        description: 'Demonstrates the ability to invent, validate, and publish novel AI system techniques or operators, ' +
          'such as retrieval algorithms, memory architectures, ranking strategies, or evaluation frameworks'
      },
      {
        code: 'L5-9',
        description: 'Demonstrates the ability to set organization-wide AI engineering strategy, including architecture, ' +
          'governance, model and data policy, evaluation standards, deployment practices, risk management, and mentoring'
      }
    ],
    goal: 'Demonstrates ability to design and architect database systems, create scalable software architectures, manage deployment pipelines and CI/CD, administer infrastructure and servers, implement complex security solutions (SSO), work with low-level protocols and enterprise systems, and provide technical leadership through mentoring and code reviews.'
  }
];

const CareerPathPanel: React.FC<CareerPathPanelProps> = ({ employeeId, employeeName, isAdmin = false }) => {
  const [progress, setProgress] = useState<CareerPathProgress>({});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    fetchProgress();
  }, [employeeId]);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const response = await axios.get<CareerPathProgress>(
        `${API_URL}/career-path/${employeeId}`
      );
      setProgress(response.data);
    } catch (err) {
      console.error('Error fetching career path progress:', err);
      setProgress({});
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (levelCode: string) => {
    try {
      setToggling(levelCode);
      const response = await axios.post(
        `${API_URL}/career-path/${employeeId}/toggle`,
        { levelCode }
      );
      
      // Update local state
      setProgress(prev => ({
        ...prev,
        [levelCode]: {
          completed: response.data.completed === 1,
          completed_at: response.data.completed_at
        }
      }));
    } catch (err) {
      console.error('Error toggling career path completion:', err);
      alert('Failed to update completion status. Please try again.');
    } finally {
      setToggling(null);
    }
  };

  const isCompleted = (levelCode: string): boolean => {
    return progress[levelCode]?.completed === true;
  };

  const getLevelProgress = (level: CareerPathPrintLevel): { completed: number; total: number } => {
    const completed = level.subLevels.filter(sub => isCompleted(sub.code)).length;
    return { completed, total: level.subLevels.length };
  };

  const handlePrint = useCallback(() => {
    try {
      printCareerPathSheet(employeeName, CAREER_LEVELS, progress, 'print');
    } catch {
      alert('Could not open the print dialog. Please try again.');
    }
  }, [employeeName, progress]);

  const handleExportPdf = useCallback(() => {
    try {
      printCareerPathSheet(employeeName, CAREER_LEVELS, progress, 'pdf');
    } catch {
      alert('Could not open the print dialog. Please try again.');
    }
  }, [employeeName, progress]);

  if (loading) {
    return (
      <div className="career-path-panel">
        <div className="career-path-loading">Loading career path progress...</div>
      </div>
    );
  }

  return (
    <div className="career-path-panel">
      <div className="career-path-header">
        <div className="career-path-header-text">
          <h3>Career Progression Scale</h3>
          <p>L1-L5 Developer Career Path for {employeeName}</p>
        </div>
        <div className="career-path-actions">
          <button
            type="button"
            className="btn btn-secondary career-path-action-btn"
            onClick={handlePrint}
            title="Open a printable career path sheet"
          >
            Print
          </button>
          <button
            type="button"
            className="btn btn-primary career-path-action-btn"
            onClick={handleExportPdf}
            title="Print dialog — choose Save as PDF as the destination"
          >
            Export PDF
          </button>
        </div>
      </div>

      <div className="career-levels-container">
        {CAREER_LEVELS.map((level) => {
          const levelProgress = getLevelProgress(level);
          const progressPercent = (levelProgress.completed / levelProgress.total) * 100;

          return (
            <div key={level.level} className="career-level-card">
              <div className="career-level-header">
                <span className="career-level-badge">{level.level}</span>
                <div className="career-level-title-section">
                  <h4>{level.title}</h4>
                  <p className="career-level-description">{level.description}</p>
                  <div className="career-level-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progressPercent}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {levelProgress.completed} / {levelProgress.total} completed
                    </span>
                  </div>
                </div>
              </div>

              <div className="career-sublevels">
                {level.subLevels.map((subLevel) => {
                  const completed = isCompleted(subLevel.code);
                  const isToggling = toggling === subLevel.code;

                  return (
                    <div 
                      key={subLevel.code} 
                      className={`career-sublevel ${completed ? 'completed' : ''}`}
                    >
                      <label className="sublevel-checkbox-label">
                        <input
                          type="checkbox"
                          checked={completed}
                          onChange={() => isAdmin && handleToggle(subLevel.code)}
                          disabled={isToggling || !isAdmin}
                          className="sublevel-checkbox"
                          style={!isAdmin ? { cursor: 'default' } : undefined}
                        />
                        <span className="sublevel-code">{subLevel.code}</span>
                      </label>
                      <p className="sublevel-description">{subLevel.description}</p>
                      {isToggling && <span className="sublevel-saving">Saving...</span>}
                    </div>
                  );
                })}
              </div>

              {level.goal && (
                <div className="career-goal">
                  <strong>Goal for {level.level}:</strong>
                  <p>{level.goal}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CareerPathPanel;
